/**
 * river_index.ts — THE RIVER TABLE AND ITS TEST, one copy for both sides (2026-10-01, "make it so ponds are included,
 * unless it breaks another feature"). MIRRORED byte-for-byte: shared/river_index.ts (the client, the bake) ≡
 * server/src/lib/river_index.ts (the module); `verify:placement-water` compares the files. No imports.
 *
 * The server judges rivers on a ROUNDED table baked from the hydrology file the client draws (positions to the metre,
 * widths to the half metre — `bake:water-index` → water_index.generated.ts). Until today the rounding lived in the bake
 * script and the index and its test in the server's water_index.ts, so the client could only approximate the server's
 * answer. A placement judge that must never show GREEN where the server refuses needs the SAME answer: the three steps
 * live here now, and
 *   the BAKE     rounds a decoded river with `riverTablePoints` (the generated table is byte-identical to before),
 *   the SERVER   decodes its table and calls `buildRiverIndex` / `riverIndexAt` (water_index.ts — the code moved, not
 *                changed), and
 *   the CLIENT   rounds the rivers it already holds (hydrology_load.loadedRivers) with the same function and builds
 *                the same index (client/src/lib/placement_water.ts) — the server's table, rebuilt, not approximated.
 *
 * THE CARVE TABLE (2026-10-01, "fix the 7% pond points that show no rendered water"). The rounded table answers "is
 * this point in a channel"; it cannot answer "what ground does the client DRAW here" — the channels are carved into the
 * client's terrain (and raise a bed across a hollow), and that carve decides where the lake bake's ponds stand. The
 * server now carries the river network EXACTLY as the client registers it: `encodeCarveTable` packs the hydrology
 * file's own integers (positions in decimetres, water in centimetres, width and depth × 10) with the still-water flags
 * the client sets at decode, `decodeCarveTable` returns `decodeHydrologyRivers(file)` bit for bit
 * (`verify:pond-parity` compares all of it). No rounding: the same doubles reach the same carve on both sides.
 */

/** The decoded table and its 128 m cell index (CSR: sorted cell keys → runs of segment ids). */
export interface RiverIndex { x: Float64Array; z: Float64Array; hw: Float32Array; seg: Uint8Array; keys: Float64Array; off: Int32Array; ids: Int32Array; cell: number }
/** One table point: x, z (whole metres) and the width × 2 (so the half width is a quarter of it). */
export type RiverTablePoint = [number, number, number];

export const RIVER_INDEX_CELL_M = 128;
/** A stored step is an int16 (the generated table's deltas); a longer one is split. */
export const RIVER_TABLE_I16 = 32767;

const cellKey = (cx: number, cz: number): number => (cx + 16384) * 32768 + (cz + 16384);

/**
 * One decoded river (flat rows `stride` wide: x at +0, z at +1, the width at +3 — hydrology_bake's RIVER_STRIDE
 * layout) → the table's points. Positions round to the metre, the width × 2 to an integer in [1, 32767]; a step longer
 * than an int16 is split (it never is at today's 8 m Douglas–Peucker, but a rebake must not wrap). A river of fewer
 * than two points has no segment and gives none.
 */
export function riverTablePoints(r: ArrayLike<number>, stride: number): RiverTablePoint[] {
  const m = Math.floor(r.length / stride);
  const pts: RiverTablePoint[] = [];
  if (m < 2) return pts;
  for (let k = 0; k < m; k++) {
    const o = k * stride;
    const x = Math.round(r[o]), z = Math.round(r[o + 1]);
    const w2 = Math.max(1, Math.min(RIVER_TABLE_I16, Math.round(r[o + 3] * 2)));
    if (pts.length) {
      const [px, pz, pw] = pts[pts.length - 1];
      const steps = Math.ceil(Math.max(Math.abs(x - px), Math.abs(z - pz)) / RIVER_TABLE_I16);
      for (let s = 1; s < steps; s++) pts.push([Math.round(px + ((x - px) * s) / steps), Math.round(pz + ((z - pz) * s) / steps), Math.round(pw + ((w2 - pw) * s) / steps)]);
    }
    pts.push([x, z, w2]);
  }
  return pts;
}

/** The table's flat arrays from its rivers, in order: `seg[i]` = 1 when point i starts a segment (i → i + 1). */
export function riverTableArrays(rivers: ReadonlyArray<ReadonlyArray<RiverTablePoint>>): { x: Float64Array; z: Float64Array; hw: Float32Array; seg: Uint8Array } {
  let total = 0;
  for (const pts of rivers) total += pts.length;
  const x = new Float64Array(total), z = new Float64Array(total), hw = new Float32Array(total), seg = new Uint8Array(total);
  let p = 0;
  for (const pts of rivers) {
    const n = pts.length;
    for (let k = 0; k < n; k++) {
      x[p] = pts[k][0]; z[p] = pts[k][1]; hw[p] = pts[k][2] / 4; // stored as width × 2 → half width = /4
      seg[p] = k < n - 1 ? 1 : 0;
      p++;
    }
  }
  return { x, z, hw, seg };
}

/** Rasterise every segment's reach (its bbox grown by the wider half width + `reach`, the largest slack a caller
 *  will ask with) into 128 m cells. */
export function buildRiverIndex(x: Float64Array, z: Float64Array, hw: Float32Array, seg: Uint8Array, reach: number): RiverIndex {
  const total = x.length;
  const keyList: number[] = [], idList: number[] = [];
  for (let i = 0; i < total; i++) {
    if (!seg[i]) continue;
    const g = Math.max(hw[i], hw[i + 1]) + reach;
    const cx0 = Math.floor((Math.min(x[i], x[i + 1]) - g) / RIVER_INDEX_CELL_M), cx1 = Math.floor((Math.max(x[i], x[i + 1]) + g) / RIVER_INDEX_CELL_M);
    const cz0 = Math.floor((Math.min(z[i], z[i + 1]) - g) / RIVER_INDEX_CELL_M), cz1 = Math.floor((Math.max(z[i], z[i + 1]) + g) / RIVER_INDEX_CELL_M);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) { keyList.push(cellKey(cx, cz)); idList.push(i); }
  }
  const order = new Int32Array(keyList.length);
  for (let i = 0; i < order.length; i++) order[i] = i;
  order.sort((a, b) => keyList[a] - keyList[b] || idList[a] - idList[b]);
  const keysTmp: number[] = [], offTmp: number[] = [];
  const ids = new Int32Array(order.length);
  for (let i = 0; i < order.length; i++) {
    const k = keyList[order[i]];
    if (keysTmp.length === 0 || keysTmp[keysTmp.length - 1] !== k) { keysTmp.push(k); offTmp.push(i); }
    ids[i] = idList[order[i]];
  }
  offTmp.push(order.length);
  return { x, z, hw, seg, keys: Float64Array.from(keysTmp), off: Int32Array.from(offTmp), ids, cell: RIVER_INDEX_CELL_M };
}

/** The river the point stands in: the nearest segment within its half width + `slack` (≤ the index's reach), else null. */
export function riverIndexAt(ix: RiverIndex, x: number, z: number, slack: number): { dist: number; halfWidth: number } | null {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  const key = cellKey(Math.floor(x / ix.cell), Math.floor(z / ix.cell));
  let lo = 0, hi = ix.keys.length - 1, at = -1;
  while (lo <= hi) { const mid = (lo + hi) >> 1; const k = ix.keys[mid]; if (k === key) { at = mid; break; } if (k < key) lo = mid + 1; else hi = mid - 1; }
  if (at < 0) return null;
  let best: { dist: number; halfWidth: number } | null = null;
  for (let j = ix.off[at]; j < ix.off[at + 1]; j++) {
    const i = ix.ids[j];
    const ax = ix.x[i], az = ix.z[i], bx = ix.x[i + 1], bz = ix.z[i + 1];
    const dx = bx - ax, dz = bz - az, L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
    const d = Math.hypot(x - (ax + dx * t), z - (az + dz * t));
    const hw = ix.hw[i] + (ix.hw[i + 1] - ix.hw[i]) * t;
    if (d <= hw + slack && (!best || d - hw < best.dist - best.halfWidth)) best = { dist: d, halfWidth: hw };
  }
  return best;
}

// ── THE CARVE TABLE ──────────────────────────────────────────────────────────────────────────────────────────────

/** Integers per table row and per decoded point (the terrain registry's RIVER_STRIDE: x, z, water, width, depth, flags). */
export const CARVE_STRIDE = 6;
/** A row whose flags column holds this carries part of a step too long for an int16 and yields no point. */
export const CARVE_CONTINUE = -1;
/** The widest value a row may hold (the generated table stores little-endian int16). */
export const CARVE_I16 = 32767;

/**
 * Pack rivers for the generated table. `raw` = each river as the hydrology file stores it (CARVE_STRIDE integers per
 * point: dx, dz in decimetres and dwater in centimetres from the previous point — the first point's are absolute —
 * then width × 10, depth × 10, flags); `flags` = each river's FINAL flags per point (the file's own plus the
 * still-water flags the decode adds). → `heads` [x0, z0, water0, points] per river and `vals`, CARVE_STRIDE int16 per
 * row: the deltas (the first point's are 0), width × 10, depth × 10, flags. A step longer than an int16 is spread over
 * CARVE_CONTINUE rows first — whole integers, so nothing is rounded.
 */
export function encodeCarveTable(raw: ReadonlyArray<ArrayLike<number>>, flags: ReadonlyArray<ArrayLike<number>>): { heads: number[]; vals: number[] } {
  const heads: number[] = [], vals: number[] = [];
  const clamp = (v: number): number => (v > CARVE_I16 ? CARVE_I16 : v < -CARVE_I16 ? -CARVE_I16 : v);
  for (let r = 0; r < raw.length; r++) {
    const e = raw[r], f = flags[r], n = Math.floor(e.length / CARVE_STRIDE);
    heads.push(n ? e[0] : 0, n ? e[1] : 0, n ? e[2] : 0, n);
    for (let k = 0; k < n; k++) {
      const o = k * CARVE_STRIDE;
      let dx = k ? e[o] : 0, dz = k ? e[o + 1] : 0, dw = k ? e[o + 2] : 0;
      while (dx > CARVE_I16 || dx < -CARVE_I16 || dz > CARVE_I16 || dz < -CARVE_I16 || dw > CARVE_I16 || dw < -CARVE_I16) {
        const px = clamp(dx), pz = clamp(dz), pw = clamp(dw);
        vals.push(px, pz, pw, 0, 0, CARVE_CONTINUE);
        dx -= px; dz -= pz; dw -= pw;
      }
      vals.push(dx, dz, dw, e[o + 3], e[o + 4], f[k]);
    }
  }
  return { heads, vals };
}

/** The rivers a carve table holds, as the client's `decodeHydrologyRivers` returns them: flat CARVE_STRIDE doubles per
 *  point — x, z (m), water (m), width (m), depth (m), flags — with the decode's own arithmetic (integer sums, then one
 *  division), so every double is the client's. */
export function decodeCarveTable(heads: ArrayLike<number>, vals: ArrayLike<number>): number[][] {
  const out: number[][] = [];
  let d = 0;
  for (let r = 0; r + 3 < heads.length; r += 4) {
    let x = heads[r], z = heads[r + 1], w = heads[r + 2];
    const n = heads[r + 3];
    const pts = new Array<number>(n * CARVE_STRIDE);
    let k = 0;
    while (k < n && d + CARVE_STRIDE <= vals.length) {
      x += vals[d]; z += vals[d + 1]; w += vals[d + 2];
      if (vals[d + 5] !== CARVE_CONTINUE) {
        const o = k * CARVE_STRIDE;
        pts[o] = x / 10; pts[o + 1] = z / 10; pts[o + 2] = w / 100;
        pts[o + 3] = vals[d + 3] / 10; pts[o + 4] = vals[d + 4] / 10; pts[o + 5] = vals[d + 5];
        k++;
      }
      d += CARVE_STRIDE;
    }
    out.push(pts);
  }
  return out;
}
